import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { InsightsPromptFunnelsResponse } from '@brand24-original/llm-types';
import { GET_INSIGHTS_PROMPT_FUNNELS } from '../../query/insights/keys';
import { getInsightsPromptFunnels } from '../../services/insights/InsightsStatsService';
import { ChatType } from '../../../types/monitor/ChatType';

export interface InsightsPromptFunnelsParams {
  projectId: number;
  chatType: ChatType;
  from: Date | null;
  to: Date | null;
  page: number;
  rows: number;
  monitorPromptIds?: number[];
  filterChatTypes?: string[];
  /**
   * The analyzed brand (project.firmName). Key-only: the backend reads it from the project, this just
   * forces a refetch when the ChangeKeywordBox switches it.
   */
  brand?: string;
}

export const useGetInsightsPromptFunnels = (params: InsightsPromptFunnelsParams | null) => {
  const fromTimestamp = params?.from?.getTime() ?? null;
  const toTimestamp = params?.to?.getTime() ?? null;

  const query = useQuery<InsightsPromptFunnelsResponse>({
    queryKey: [
      GET_INSIGHTS_PROMPT_FUNNELS,
      params?.projectId,
      params?.chatType,
      fromTimestamp,
      toTimestamp,
      params?.page,
      params?.rows,
      params?.monitorPromptIds,
      params?.filterChatTypes,
      params?.brand,
    ],
    // `enabled` gates on params/from/to, so the assertions below cannot fire at runtime.
    queryFn: () =>
      getInsightsPromptFunnels(
        params!.projectId,
        params!.chatType,
        params!.from!,
        params!.to!,
        params!.page,
        params!.rows,
        params!.monitorPromptIds,
        params!.filterChatTypes
      ),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    // Keep the previous page while the next one loads: the matrix swaps only the TABLE for a skeleton
    // (see isPlaceholderData below) and keeps its controls mounted — without the placeholder `data`
    // would drop to null and the BrandPicker/metric switcher would unmount on every page change.
    placeholderData: keepPreviousData,
    enabled: !!params && !!params.from && !!params.to && !!params.chatType && !!params.projectId,
  });

  return {
    insightsPromptFunnels: query.data ?? null,
    // isPending, not isLoading — patrz komentarz w useGetInsightsOverview.
    isLoading: query.isPending,
    // True while `data` still belongs to the previous page/scope — the page-change loading state.
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
  };
};
